import { apiClient } from './client';

/**
 * 인사기록 — 학력 · 경력 API (인사, migration 132 · 시안 A19).
 *
 * 서버: `apps/chat-server/src/modules/hr/records.routes.ts`.
 *
 * ★★ **최종학력은 서버가 골라 준다** — 화면이 순위를 갖지 않는다(학위 순위는 서버 `records.ts`).
 * ★ **확인(confirmedAt)과 증빙(hasProof)은 다른 축이다.** 확인은 담당자가 «맞다» 고 한 것,
 *   증빙은 서류가 붙은 것 — 하나로 합치면 «서류 없이 확인» 을 말할 수 없다.
 * ★ **옛 서버에는 이 라우트가 없다**(404). 부르는 쪽이 실패를 삼키고 화면을 접는다.
 */

export const EDUCATION_LEVELS = ['HIGH_SCHOOL', 'ASSOCIATE', 'BACHELOR', 'MASTER', 'DOCTOR', 'OTHER'] as const;
export type EducationLevel = (typeof EDUCATION_LEVELS)[number];
export const EDUCATION_LEVEL_LABEL: Record<string, string> = {
  HIGH_SCHOOL: '고교', ASSOCIATE: '전문학사', BACHELOR: '학사', MASTER: '석사', DOCTOR: '박사', OTHER: '기타',
};

export const EDUCATION_STATES = ['GRADUATED', 'ENROLLED', 'DROPPED', 'EXPECTED'] as const;
export type EducationState = (typeof EDUCATION_STATES)[number];
export const EDUCATION_STATE_LABEL: Record<string, string> = {
  GRADUATED: '졸업', ENROLLED: '재학', DROPPED: '중퇴', EXPECTED: '졸업 예정',
};

/** 증빙 — 하나도 없음 · 일부 · 전부. **항목이 없으면 `NONE`** 이다. */
export type ProofState = 'ALL' | 'SOME' | 'NONE';

/** 사람 하나의 요약 — 목록의 한 줄과 상세의 머리가 **같은 값**을 쓴다. */
export interface RecordSummaryDto {
  eduCount: number;
  careerCount: number;
  careerMonths: number;
  careerYears: number;
  finalLevel: string | null;
  finalLevelLabel: string | null;
  major: string | null;
  proof: ProofState;
  /** 담당자가 아직 확인하지 않은 항목 수 — 「본인 입력」 배지의 근거. */
  unconfirmed: number;
}

export interface RecordRowDto extends RecordSummaryDto {
  userId: string;
  name: string;
  employeeNumber: string | null;
  position: string | null;
  teamId: string | null;
  teamName: string | null;
  hireDate: string | null;
  employmentStatus: string;
}

export interface RecordListDto {
  total: number;
  summary: {
    withCareer: number;
    eduMissing: number;
    /** 증빙 없는 **항목 수**(사람 수가 아니다). */
    proofMissingItems: number;
    /** 경력이 있는 사람만 나눈 평균. */
    avgCareerYears: number;
  };
  rows: RecordRowDto[];
}

export interface EducationDto {
  id: string;
  userId: string;
  level: string;
  levelLabel: string;
  schoolName: string;
  major: string | null;
  startYm: string | null;
  endYm: string | null;
  state: string;
  stateLabel: string;
  hasProof: boolean;
  confirmedAt: string | null;
  /** 본인이 적었나 — 서버가 `createdBy` 로 판정한다. */
  selfEntered: boolean;
  note: string | null;
  createdAt: string;
}

export interface CareerDto {
  id: string;
  userId: string;
  companyName: string;
  position: string | null;
  duty: string | null;
  startYm: string;
  endYm: string;
  months: number;
  years: number;
  hasProof: boolean;
  confirmedAt: string | null;
  selfEntered: boolean;
  note: string | null;
  createdAt: string;
}

export interface RecordDetailDto {
  user: {
    userId: string; name: string; employeeNumber: string | null;
    position: string | null; teamName: string | null; hireDate: string | null;
  };
  educations: EducationDto[];
  careers: CareerDto[];
  summary: RecordSummaryDto;
}

export type RecordFilter = 'all' | 'eduMissing' | 'proofMissing';

export async function listRecords(params?: {
  search?: string; teamId?: string; filter?: RecordFilter;
}): Promise<RecordListDto> {
  const { data } = await apiClient.get<RecordListDto>('/hr/records', { params });
  return data;
}

/** 한 사람의 학력·경력. **본인도 부를 수 있다**(남의 것은 담당자만 — 서버가 가른다). */
export async function getRecords(userId: string): Promise<RecordDetailDto> {
  const { data } = await apiClient.get<RecordDetailDto>(`/hr/records/${userId}`);
  return data;
}

export interface EducationInput {
  level: string;
  schoolName: string;
  major?: string | null;
  startYm?: string | null;
  endYm?: string | null;
  state?: string;
  hasProof?: boolean;
  confirmed?: boolean;
  note?: string | null;
}

export interface CareerInput {
  companyName: string;
  position?: string | null;
  duty?: string | null;
  startYm: string;
  endYm: string;
  hasProof?: boolean;
  confirmed?: boolean;
  note?: string | null;
}

export async function createEducation(userId: string, body: EducationInput): Promise<EducationDto> {
  const { data } = await apiClient.post<{ education: EducationDto }>(`/hr/records/${userId}/educations`, body);
  return data.education;
}

/** `confirmed` 를 **안 보내면 확인 도장을 건드리지 않는다** — 고쳤다고 확인이 풀리면 안 된다. */
export async function updateEducation(id: string, body: Partial<EducationInput>): Promise<EducationDto> {
  const { data } = await apiClient.patch<{ education: EducationDto }>(`/hr/educations/${id}`, body);
  return data.education;
}

export async function deleteEducation(id: string): Promise<void> {
  await apiClient.delete(`/hr/educations/${id}`);
}

export async function createCareer(userId: string, body: CareerInput): Promise<CareerDto> {
  const { data } = await apiClient.post<{ career: CareerDto }>(`/hr/records/${userId}/careers`, body);
  return data.career;
}

export async function updateCareer(id: string, body: Partial<CareerInput>): Promise<CareerDto> {
  const { data } = await apiClient.patch<{ career: CareerDto }>(`/hr/careers/${id}`, body);
  return data.career;
}

export async function deleteCareer(id: string): Promise<void> {
  await apiClient.delete(`/hr/careers/${id}`);
}

/** `2015-03 ~ 2017-02` — 없으면 «—». 화면 세 곳이 같은 문구를 쓴다. */
export function periodText(startYm: string | null, endYm: string | null): string {
  if (!startYm && !endYm) return '—';
  return `${startYm ?? '?'} ~ ${endYm ?? '현재'}`;
}
